import { useState, useEffect, useCallback, useRef } from 'react';
import { getCookie } from '../components/utils/utils';

interface EnhancementStatus {
  enabled: boolean;
  credits: number;
  price_per_package: number;
  credits_per_package: number;
}

interface ImageData {
  id: string;
  url: string;
  thumbnailUrl: string;
  uploadedAt?: string;
  order?: number;
  isUploading?: boolean;
  isEnhancing?: boolean;
  uploadProgress?: number;
  error?: string;
  originalUrl?: string;
  enhancedUrl?: string;
  originalThumbnailUrl?: string;
  enhancedThumbnailUrl?: string;
  enhancementFailed?: boolean;
  isDisplayOptimized?: boolean;
}

interface FormData {
  description: string;
  size: string;
  rooms: string;
  floor: string;
  hasElevator: boolean;
  hasBalcony: boolean;
  renovationStatus: string;
  buildingType: string;
  images: ImageData[];
  timestamp?: number;
}

interface UseOffMarketFormProps {
  actionType: 'buy' | 'sell';
  buildingId?: string;
  isLoggedIn: boolean;
  isOpenModal: boolean;
  isLandingPage?: boolean;
}

export interface BuyingTemplate {
  description: string;
  size: string;
  rooms: string;
  floor: string;
  hasElevator: boolean;
  hasBalcony: boolean;
  renovationStatus: string;
  buildingType: string;
  savedAt?: string;
  lastUsedAt?: string;
}

export const useOffMarketForm = ({
  actionType,
  buildingId,
  isLoggedIn,
  isOpenModal,
  isLandingPage = false
}: UseOffMarketFormProps) => {
  const [enhancementStatus, setEnhancementStatus] = useState<EnhancementStatus | null>(null);
  const [isPurchasingCredits, setIsPurchasingCredits] = useState<boolean>(false);
  const restorationAttemptedRef = useRef<boolean>(false);
  const [formData, setFormData] = useState<FormData>({
    description: '',
    size: '',
    rooms: '',
    floor: '',
    hasElevator: false,
    hasBalcony: false,
    renovationStatus: '',
    buildingType: '',
    images: []
  });

  // Template state management - only for buying action
  const [buyingTemplate, setBuyingTemplate] = useState<BuyingTemplate | null>(null);
  const [hasTemplate, setHasTemplate] = useState<boolean>(false);
  const [isLoadingTemplate, setIsLoadingTemplate] = useState<boolean>(false);
  const [isUsingTemplate, setIsUsingTemplate] = useState<boolean>(false);
  const [shouldSaveTemplate, setShouldSaveTemplate] = useState<boolean>(true);
  const [isTemplateAnimating, setIsTemplateAnimating] = useState<boolean>(false);

  // If the modal is reused while switching actionType (buy -> sell), make sure
  // any "buying template" UI state can't leak into the sell flow.
  useEffect(() => {
    if (actionType !== 'buy') {
      setIsUsingTemplate(false);
      setIsTemplateAnimating(false);
      setShouldSaveTemplate(true);
    }
  }, [actionType]);

  // LocalStorage key for preserving form data - include buildingId if available
  const FORM_DATA_STORAGE_KEY = buildingId 
    ? `off_market_form_${actionType}_${buildingId}` 
    : (isLandingPage ? `landing_page_${actionType}_form_data` : `could_${actionType}_modal_form_data`);
    
  // Check URL params immediately on hook initialization to catch payment returns
  const initialParams = new URLSearchParams(window.location.search);
  const initialPaymentStatus = initialParams.get('payment');
  const initialSessionId = initialParams.get('session_id');
  if (initialPaymentStatus || initialSessionId) {
    console.log('Payment parameters detected on init - payment:', initialPaymentStatus, 'session_id:', initialSessionId);
  }

  // Fetch enhancement status
  const fetchEnhancementStatus = useCallback(async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/ai/enhancement-status`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'X-CSRF-TOKEN': getCookie('csrf_access_token'),
        },
      });

      if (response.ok) {
        const data = await response.json();
        setEnhancementStatus(data);
      }
    } catch (error) {
      console.error('Error fetching enhancement status:', error);
    }
  }, []);

  // Save form data to localStorage
  const saveFormDataToLocalStorage = useCallback(() => {
    const dataToSave = {
      ...formData,
      // For images, we'll save the data but not blob URLs
      images: (formData.images || []).map(img => ({
        ...img,
        url: img.url?.startsWith('blob:') ? null : img.url,
        thumbnailUrl: img.thumbnailUrl?.startsWith('blob:') ? null : img.thumbnailUrl,
      })),
      timestamp: Date.now()
    };
    
    console.log('Saving form data to localStorage with key:', FORM_DATA_STORAGE_KEY, 'data:', dataToSave);
    localStorage.setItem(FORM_DATA_STORAGE_KEY, JSON.stringify(dataToSave));
    console.log('Form data saved successfully with timestamp:', dataToSave.timestamp);
    
    // Debug: Verify it was actually saved
    const verifyData = localStorage.getItem(FORM_DATA_STORAGE_KEY);
    console.log('Verification - Data actually saved:', !!verifyData, 'Length:', verifyData?.length);
  }, [formData, FORM_DATA_STORAGE_KEY]);

  // Restore form data from localStorage
  const restoreFormDataFromLocalStorage = useCallback((): boolean => {
    console.log('Attempting to restore form data with key:', FORM_DATA_STORAGE_KEY);
    console.log('Building ID being used:', buildingId);
    
    // Debug: Log all localStorage keys that start with 'off_market'
    const allKeys = Object.keys(localStorage).filter(key => key.includes('off_market'));
    console.log('All off_market keys in localStorage:', allKeys);
    
    const savedDataStr = localStorage.getItem(FORM_DATA_STORAGE_KEY);
    if (!savedDataStr) {
      console.log('No saved form data found for key:', FORM_DATA_STORAGE_KEY);
      return false;
    }
    
    try {
      const savedData = JSON.parse(savedDataStr);
      console.log('Found saved form data:', savedData);
      
      // Check if data is less than 30 minutes old
      const thirtyMinutes = 30 * 60 * 1000;
      if (Date.now() - savedData.timestamp > thirtyMinutes) {
        console.log('Saved data is too old, discarding');
        localStorage.removeItem(FORM_DATA_STORAGE_KEY);
        return false;
      }
      
      // Restore all form fields
      const restoredData = {
        description: savedData.description || '',
        size: savedData.size || '',
        rooms: savedData.rooms || '',
        floor: savedData.floor || '',
        hasElevator: savedData.hasElevator || false,
        hasBalcony: savedData.hasBalcony || false,
        renovationStatus: savedData.renovationStatus || '',
        buildingType: savedData.buildingType || '',
        // Restore images (filter out ones with missing URLs)
        images: (savedData.images && savedData.images.length > 0) 
          ? savedData.images.filter((img: any) => img.url && !img.isUploading)
          : []
      };
      
      console.log('Restoring form data:', restoredData);
      setFormData(restoredData);
      
      // Clear the saved data after restoring (only on successful restoration)
      // Delay removal to ensure it's not needed for subsequent attempts
      setTimeout(() => {
        localStorage.removeItem(FORM_DATA_STORAGE_KEY);
        console.log('Removed saved form data from localStorage after successful restoration');
      }, 2000);
      
      return true;
    } catch (error) {
      console.error('Error restoring form data:', error);
      localStorage.removeItem(FORM_DATA_STORAGE_KEY);
      return false;
    }
  }, [FORM_DATA_STORAGE_KEY, setFormData, buildingId]);

  // Handle buying credits
  const handleBuyCredits = useCallback(async () => {
    setIsPurchasingCredits(true);
    
    // Save form data before redirecting to Stripe
    console.log('handleBuyCredits called - actionType:', actionType, 'buildingId:', buildingId, 'isLandingPage:', isLandingPage);
    console.log('Saving form data before Stripe redirect:', formData);
    saveFormDataToLocalStorage();

    try {
      // Build return URL with appropriate parameters
      let returnUrlPath: string;
      const currentUrl = new URL(window.location.href);
      console.log('Current URL when handleBuyCredits called:', currentUrl.toString());
      console.log('Building return URL - isLandingPage:', isLandingPage, 'buildingId:', buildingId);
      
      if (isLandingPage) {
        // For landing page, use the /could-sell or /could-buy paths
        const basePath = actionType === 'sell' ? '/could-sell' : '/could-buy';
        returnUrlPath = basePath;
      } else if (buildingId) {
        // For building modal context with buildingId, ensure we have the building-id in URL
        if (!currentUrl.searchParams.has('building-id')) {
          currentUrl.searchParams.set('building-id', buildingId);
        }
        currentUrl.searchParams.set('modal', actionType);
        returnUrlPath = currentUrl.pathname + currentUrl.search;
      } else {
        // Fallback: preserve current URL with modal parameter
        currentUrl.searchParams.set('modal', actionType);
        returnUrlPath = currentUrl.pathname + currentUrl.search;
      }
      
      // Save the return URL to localStorage so payment pages can use it
      console.log('Saving return URL to localStorage:', returnUrlPath);
      localStorage.setItem('stripe_return_url', returnUrlPath);
      
      // Build full URL for Stripe
      const fullReturnUrl = new URL(returnUrlPath, window.location.origin).toString();
      console.log('Full return URL for Stripe:', fullReturnUrl);
      
      // Extra logging for Building Modal flow
      if (!isLandingPage) {
        console.log('Building Modal flow - verifying URL parameters:');
        const testUrl = new URL(fullReturnUrl);
        console.log('  - modal param:', testUrl.searchParams.get('modal'));
        console.log('  - building-id param:', testUrl.searchParams.get('building-id'));
      }
      
      const requestBody = {
        return_url: fullReturnUrl // Send the full URL for Stripe
      };
      console.log('Request body being sent to backend:', requestBody);
      
      const response = await fetch(`${process.env.REACT_APP_API_URL}/payments/create-checkout-session`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-TOKEN': getCookie('csrf_access_token'),
        },
        body: JSON.stringify(requestBody)
      });

      if (response.ok) {
        const data = await response.json();
        // Redirect to Stripe Checkout
        window.location.href = data.checkout_url;
      } else {
        const error = await response.json();
        console.error('Failed to create checkout session:', error);
        alert('Purchase failed. Please try again.');
      }
    } catch (error) {
      console.error('Error purchasing credits:', error);
      alert('Purchase failed. Please try again.');
    } finally {
      setIsPurchasingCredits(false);
    }
  }, [saveFormDataToLocalStorage, actionType, buildingId, isLandingPage]);

  // Check for payment returns and restore form data
  const checkPaymentReturn = useCallback(() => {
    console.log('checkPaymentReturn called');
    console.log('Current URL:', window.location.href);
    const params = new URLSearchParams(window.location.search);
    const paymentStatus = params.get('payment');
    const sessionId = params.get('session_id');
    const modalType = params.get('modal');
    const hasBuildingId = params.has('building-id');
    console.log('Payment status:', paymentStatus, 'Session ID:', sessionId, 'Modal type:', modalType, 'Action type:', actionType, 'Has building-id:', hasBuildingId);
    console.log('restorationAttemptedRef.current:', restorationAttemptedRef.current);
    
    // Check for payment parameter OR session_id (which indicates a Stripe return)
    if (((paymentStatus === 'success' || paymentStatus === 'cancelled') || sessionId) && 
        (!modalType || modalType === actionType) &&
        !restorationAttemptedRef.current) {
      // Determine the actual payment status
      const actualPaymentStatus = paymentStatus || (sessionId ? 'success' : null);
      
      console.log('Payment return detected, attempting to restore form data');
      console.log('Using storage key:', FORM_DATA_STORAGE_KEY);
      console.log('Actual payment status:', actualPaymentStatus);
      
      // Special logging for cancelled payments
      if (actualPaymentStatus === 'cancelled') {
        console.log('Payment was CANCELLED - restoring form data from before payment attempt');
      }
      
      // Try to restore form data
      const dataRestored = restoreFormDataFromLocalStorage();
      if (dataRestored) {
        console.log(`Form data successfully restored after ${actualPaymentStatus} payment`);
      } else {
        console.log('Failed to restore form data after payment - no saved data found');
      }
      
      if (actualPaymentStatus === 'success') {
        console.log('Payment successful! Credits have been added to your account.');
        // Refresh enhancement status
        fetchEnhancementStatus();
      }
      
      // Clean up URL - remove payment and session_id parameters
      const url = new URL(window.location.href);
      url.searchParams.delete('payment');
      url.searchParams.delete('session_id');
      window.history.replaceState({}, '', url.toString());
      
      // Mark that we've attempted restoration
      restorationAttemptedRef.current = true;
    }
  }, [restoreFormDataFromLocalStorage, fetchEnhancementStatus, actionType, FORM_DATA_STORAGE_KEY]);
  
  // Reset restoration flag when modal closes
  useEffect(() => {
    if (!isOpenModal) {
      restorationAttemptedRef.current = false;
    }
  }, [isOpenModal]);

  // On modal open, handle payment return and enhancement status
  useEffect(() => {
    if (isOpenModal) {
      // Check for payment return for both buy and sell actions
      checkPaymentReturn();
      
      // Also check with a small delay in case URL params are still being set
      setTimeout(() => {
        checkPaymentReturn();
      }, 100);
      
      // Fetch enhancement status for sell action only
      if (actionType === 'sell' && isLoggedIn) {
        fetchEnhancementStatus();
      }
    }
  }, [isOpenModal, isLoggedIn, actionType, fetchEnhancementStatus, checkPaymentReturn]);

  // Clear form data
  const clearFormData = useCallback(() => {
    setFormData({
      description: '',
      size: '',
      rooms: '',
      floor: '',
      hasElevator: false,
      hasBalcony: false,
      renovationStatus: '',
      buildingType: '',
      images: []
    });
    localStorage.removeItem(FORM_DATA_STORAGE_KEY);
  }, [FORM_DATA_STORAGE_KEY]);

  // Template management functions
  const fetchBuyingTemplate = useCallback(async () => {
    if (!isLoggedIn || actionType !== 'buy') return;

    // Check session storage cache first
    const cacheKey = 'buying_template_cache';
    const cached = sessionStorage.getItem(cacheKey);
    if (cached) {
      const { timestamp, data } = JSON.parse(cached);
      // Use cache if less than 10 minutes old
      if (Date.now() - timestamp < 10 * 60 * 1000) {
        console.log('Using cached buying template');
        setBuyingTemplate(data.template);
        setHasTemplate(data.hasTemplate);
        if (data.hasTemplate && data.template) {
          applyTemplateToForm(data.template);
        }
        return;
      }
    }

    setIsLoadingTemplate(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/user/buying-template`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'X-CSRF-TOKEN': getCookie('csrf_access_token'),
        },
      });

      if (response.ok) {
        const data = await response.json();
        setBuyingTemplate(data.template);
        setHasTemplate(data.hasTemplate);
        
        // Cache the response
        sessionStorage.setItem(cacheKey, JSON.stringify({
          timestamp: Date.now(),
          data
        }));

        if (data.hasTemplate && data.template) {
          applyTemplateToForm(data.template);
        }
      }
    } catch (error) {
      console.error('Error fetching buying template:', error);
    } finally {
      setIsLoadingTemplate(false);
    }
  }, [isLoggedIn, actionType]);

  const applyTemplateToForm = useCallback((template: BuyingTemplate) => {
    setIsUsingTemplate(true);
    setIsTemplateAnimating(true);
    
    // Apply template data to form with slight delay for animation
    setTimeout(() => {
      setFormData(prev => ({
        ...prev,
        description: template.description || '',
        size: template.size || '',
        rooms: template.rooms || '',
        floor: template.floor || '',
        hasElevator: template.hasElevator || false,
        hasBalcony: template.hasBalcony || false,
        renovationStatus: template.renovationStatus || '',
        buildingType: template.buildingType || '',
      }));
      
      // End animation after fields are populated
      setTimeout(() => {
        setIsTemplateAnimating(false);
      }, 600);
    }, 100);

    console.log('Applied buying template to form');
  }, []);

  const saveBuyingTemplate = useCallback(async () => {
    if (!isLoggedIn || actionType !== 'buy') return false;

    try {
      const templateData = {
        description: formData.description,
        size: formData.size,
        rooms: formData.rooms,
        floor: formData.floor,
        hasElevator: formData.hasElevator,
        hasBalcony: formData.hasBalcony,
        renovationStatus: formData.renovationStatus,
        buildingType: formData.buildingType,
      };

      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/user/buying-template`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-TOKEN': getCookie('csrf_access_token'),
        },
        body: JSON.stringify(templateData),
      });

      if (response.ok) {
        const data = await response.json();
        setBuyingTemplate(data.template);
        setHasTemplate(true);
        
        // Clear cache to force refresh on next load
        sessionStorage.removeItem('buying_template_cache');
        
        console.log('Buying template saved successfully');
        return true;
      }
    } catch (error) {
      console.error('Error saving buying template:', error);
    }
    return false;
  }, [isLoggedIn, actionType, formData]);

  const deleteBuyingTemplate = useCallback(async () => {
    if (!isLoggedIn || actionType !== 'buy') return false;

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/user/buying-template`, {
        method: 'DELETE',
        credentials: 'include',
        headers: {
          'X-CSRF-TOKEN': getCookie('csrf_access_token'),
        },
      });

      if (response.ok) {
        setBuyingTemplate(null);
        setHasTemplate(false);
        setIsUsingTemplate(false);
        // Ensure UI fields are cleared immediately
        clearFormData();
        setShouldSaveTemplate(false);
        
        // Clear cache
        sessionStorage.removeItem('buying_template_cache');
        
        console.log('Buying template deleted successfully');
        return true;
      }
    } catch (error) {
      console.error('Error deleting buying template:', error);
    }
    return false;
  }, [isLoggedIn, actionType, clearFormData]);

  // Fetch template when modal opens for buy action
  useEffect(() => {
    if (isOpenModal && actionType === 'buy' && isLoggedIn && !restorationAttemptedRef.current) {
      fetchBuyingTemplate();
    }
  }, [isOpenModal, actionType, isLoggedIn, fetchBuyingTemplate]);

  // Ensure images is always an array
  const safeFormData = {
    ...formData,
    images: Array.isArray(formData.images) ? formData.images : []
  };

  // Image version toggling (shared between both modals)
  const toggleImageVersion = useCallback((index: number) => {
    setFormData(prev => {
      const newImages = [...prev.images];
      const image = newImages[index];

      if (image && image.enhancedUrl && image.originalUrl) {
        const showEnhanced = image.url !== image.enhancedUrl;
        newImages[index] = {
          ...image,
          url: showEnhanced ? image.enhancedUrl : image.originalUrl,
          thumbnailUrl: showEnhanced
            ? (image.enhancedThumbnailUrl || image.enhancedUrl)
            : (image.originalThumbnailUrl || image.originalUrl)
        };
      }

      return { ...prev, images: newImages };
    });
  }, []);

  // Image regeneration (shared between both modals)
  const [isRegenerating, setIsRegenerating] = useState(false);

  const regenerateImage = useCallback(async (index: number) => {
    const image = safeFormData.images[index];
    if (!image || !image.id || isRegenerating) return;

    setIsRegenerating(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/ai/enhance-and-upload-image`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-TOKEN': getCookie('csrf_access_token'),
        },
        body: JSON.stringify({ imageId: image.id }),
      });

      if (!response.ok) throw new Error(`Re-enhance failed with status ${response.status}`);

      const data = await response.json();

      setFormData(prev => {
        const newImages = [...prev.images];
        if (newImages[index]) {
          newImages[index] = {
            ...newImages[index],
            enhancedUrl: data.enhancedUrl,
            enhancedThumbnailUrl: data.enhancedThumbnailUrl || data.enhancedUrl,
            url: data.enhancedUrl,
            thumbnailUrl: data.enhancedThumbnailUrl || data.enhancedUrl,
          };
        }
        return { ...prev, images: newImages };
      });

      if (enhancementStatus) {
        setEnhancementStatus({
          ...enhancementStatus,
          credits: Math.max(0, enhancementStatus.credits - 1)
        });
      }
    } catch (err) {
      console.error('Failed to regenerate image:', err);
    } finally {
      setIsRegenerating(false);
    }
  }, [safeFormData.images, isRegenerating, enhancementStatus]);

  return {
    formData: safeFormData,
    setFormData,
    enhancementStatus,
    setEnhancementStatus,
    isPurchasingCredits,
    handleBuyCredits,
    saveFormDataToLocalStorage,
    restoreFormDataFromLocalStorage,
    clearFormData,
    fetchEnhancementStatus,
    // Template-related
    buyingTemplate,
    hasTemplate,
    isLoadingTemplate,
    isUsingTemplate,
    shouldSaveTemplate,
    setShouldSaveTemplate,
    isTemplateAnimating,
    fetchBuyingTemplate,
    saveBuyingTemplate,
    deleteBuyingTemplate,
    // Image helpers
    toggleImageVersion,
    regenerateImage,
    isRegenerating
  };
};
